import differenceInCalendarDays from 'date-fns/esm/differenceInCalendarDays'
import format from 'date-fns-tz/esm/format'
import isFuture from 'date-fns/isFuture'

import { Types } from 'service-api'
import { DEFAULT_TIMEZONE, getTzDateInUtc } from './dates'
import { i18WithDefault as t } from './locales'
import { formatInTimeZone } from 'date-fns-tz'

type InvoiceAllowedAction = 'EDIT' | 'CANCEL' | 'CHECK_IN' | 'CHECK_OUT' | 'LATE_CHECK_IN' | 'LATE_CHECK_OUT' | 'VIEW'

export const getInvoiceDates = (invoice: Types.InvoiceClientDto) => {
  const { startDate, startTime, endDate, endTime } = invoice.period?.[0] ?? {}
  return {
    startDate: getTzDateInUtc(startDate, startTime, invoice.location?.timezone),
    endDate: getTzDateInUtc(endDate, endTime, invoice.location?.timezone),
  }
}

export const getInvoiceActualDates = (invoice?: Types.InvoiceAdminDto) => {
  const timezone = invoice?.location?.timezone || DEFAULT_TIMEZONE,
    period = invoice?.period?.[0],
    activity = invoice?.activities?.[0]
  return {
    startDate: activity?.startedAt || getTzDateInUtc(period?.startDate, period?.startTime, timezone) || '',
    endDate: activity?.endedAt || getTzDateInUtc(period?.endDate, period?.endTime, timezone) || '',
    hasStartTime: !!(activity?.startedAt || period?.startTime),
    hasEndTime: !!(activity?.endedAt || period?.endTime),
  }
}

export const getOfferAddons = (invoice?: Types.InvoiceAdminDto): Types.InvoiceItemAdminDto[] => {
  return invoice?.items?.filter((item) => item.offerType === 'SERVICE') || []
}

export const getPetAddons = (invoice?: Types.InvoiceAdminDto): Types.InvoiceItemAdminDto[] => {
  return invoice?.items?.filter((item) => item.offerType === 'PET') || []
}

export const getCompCreditItems = (invoice?: Types.InvoiceAdminDto): Types.InvoiceItemAdminDto[] => {
  return invoice?.items?.filter((item) => item.type === 'CREDIT') || []
}

export const getAdditionItems = (invoice?: Types.InvoiceAdminDto): Types.InvoiceItemAdminDto[] => {
  return invoice?.items?.filter((item) => item.type === 'ADDITION') || []
}

export type TInvoiceItemAddons = {
  id: string
  displayName: string
}

export const formatPetAddons = (invoiceItems?: Types.InvoiceItemAdminDto[]) => {
  return (
    invoiceItems?.reduce((acc, item) => {
      const display = `${item.displayName} (${formatPetNames(getPetsInInvoiceItem(item), false)})`
      acc.push({
        id: item.id!,
        displayName: `${display}${(item.qty ?? 1) > 1 ? `x${item.qty}` : ''}`,
      })
      return acc
    }, [] as TInvoiceItemAddons[]) || []
  )
}

export const isCheckInState = (invoice?: Types.CustomInvoiceAdminDto) =>
  ['CHECK_IN', 'LATE_CHECK_IN'].some((action) => invoice?.allowedActions?.includes(action as InvoiceAllowedAction))

export const isCheckOutState = (invoice?: Types.CustomInvoiceAdminDto) =>
  ['CHECK_OUT', 'LATE_CHECK_OUT'].some((action) => invoice?.allowedActions?.includes(action as InvoiceAllowedAction))

export const isViewState = (invoice?: Types.CustomInvoiceAdminDto) => !!invoice?.allowedActions?.includes('VIEW')

export const isCancelState = (invoice?: Types.CustomInvoiceAdminDto) => !!invoice?.allowedActions?.includes('CANCEL')

export const getPrimaryOffer = (invoice?: Types.InvoiceAdminDto): Types.InvoiceItemAdminDto | undefined => {
  return invoice?.items?.find((item) => item.offerType === 'PRIMARY')
}

export const getPrimaryServiceTypeName = (invoice?: Types.InvoiceAdminDto) => {
  return getPrimaryOffer(invoice)?.locationServiceType?.serviceType?.name
}

export const isTemperTestInvoice = (invoice?: Types.InvoiceAdminDto) => {
  return getPrimaryOffer(invoice)?.locationServiceType?.serviceType?.name === 'temper-test'
}

export const getOtherOffers = (invoice?: Types.InvoiceAdminDto): Types.InvoiceItemAdminDto[] | undefined => {
  return invoice?.items?.reduce((acc, item) => {
    if (item.offerType !== 'PRIMARY') {
      acc.push(item)
    }
    return acc
  }, [] as Types.InvoiceItemAdminDto[])
}

export const getPetsInInvoiceItem = (invoiceItem?: Types.InvoiceItemAdminDto) => {
  return (
    invoiceItem?.petRelations?.map((r) => ({
      ...r.invoicePet!,
    })) || []
  )
}

export const getPetsInvoiced = (
  invoice?: Types.InvoiceAdminDto,
  petTags: Types.TagAdminDto[] = []
): Types.InvoicePetAdminDto[] => {
  const primaryOffer = getPrimaryOffer(invoice)
  const invoicePetIds = new Set(
    (primaryOffer?.petRelations || []).map((rel) => rel.invoicePet?.id || rel.invoicePetId || '')
  )
  const petTagById = petTags.reduce<Record<string, Types.TagAdminDto>>((res, tag) => {
    if (tag.id) {
      res[tag.id] = res[tag.id] || tag
    }
    return res
  }, {})

  return (invoice?.order?.pets || [])
    .filter((invoicePet) => invoicePet.id && invoicePetIds.has(invoicePet.id))
    .map((invoicePet) => ({
      ...invoicePet,
      locationPetProfile: {
        ...invoicePet.locationPetProfile,
        locationSpecies: invoicePet.locationSpecies,
        tags: (invoicePet.locationPetProfile?.tags || []).map((tagRelation) => ({
          ...tagRelation,
          tag: petTagById[tagRelation.tagId || ''],
        })),
      },
    }))
}

export const isOvernight = (invoice?: Types.InvoiceAdminDto) =>
  !!getPrimaryOffer(invoice)?.locationServiceType?.serviceType?.overnight

export const isOvernightItem = (invoiceItem?: Types.InvoiceItemAdminDto) =>
  !!invoiceItem?.locationServiceType?.serviceType?.overnight

export const formatInvoicePeriod = (invoice?: Types.InvoiceAdminDto) => {
  const period = invoice?.period?.[0]
  if (!period) {
    return ''
  }

  const overNight = isOvernight(invoice)
  return `${format(getTzDateInUtc(period.startDate), 'MMM d')}${
    overNight ? ` - ${format(getTzDateInUtc(period.endDate), 'MMM d')}` : ''
  }`
}

export const primaryOfferName = (invoice?: Types.InvoiceAdminDto) => {
  return getPrimaryOffer(invoice)?.displayName || ''
}

export const formatPetNames = (pets?: Types.InvoicePetAdminDto[], showSex = true) => {
  const formatSex = (sex: string) => {
    const localeSex = t(`global.label.${sex}`) || ''
    return localeSex ? ` (${localeSex[0]})` : ''
  }

  return (
    pets
      ?.map((pet) => {
        const sexStr = showSex ? formatSex(pet?.locationPetProfile?.sex || '') : ''
        return `${pet?.displayName || ''}${sexStr}`
      })
      .join(', ') || ''
  )
}

export const formatInvoiceSummaryTitle = (invoice: Types.CustomInvoiceAdminDto) => {
  const timezone = invoice.location?.timezone || DEFAULT_TIMEZONE
  const offer = getPrimaryOffer(invoice),
    { startDate, endDate } = getInvoiceDates(invoice),
    overnight = isOvernight(invoice),
    pets = getPetsInvoiced(invoice)

  const date = overnight
    ? `${formatInTimeZone(startDate, timezone, 'M/d')} - ${formatInTimeZone(endDate, timezone, 'M/d')}`
    : formatInTimeZone(startDate, timezone, 'M/d')

  return `${offer?.displayName} | ${date} | ${formatPetNames(pets, false)}`
}

export const formatInvoicePeriodSimple = (invoice?: Types.InvoiceAdminDto, overNight = true) => {
  const period = invoice?.period?.[0]
  if (!period) {
    return ''
  }

  return `${format(getTzDateInUtc(period.startDate), 'M/d')}${
    overNight ? ` - ${format(getTzDateInUtc(period.endDate), 'M/d')}` : ''
  }`
}

export const daysOfStay = (periods?: Types.InvoicePeriodAdminDto[], pricingStrategy?: 'NIGHTLY' | 'DAILY' | null) => {
  if (!periods?.length || !pricingStrategy) {
    return null
  }

  const periodLength = differenceInCalendarDays(
    getTzDateInUtc(periods[0].endDate),
    getTzDateInUtc(periods[0].startDate)
  )
  return pricingStrategy === 'NIGHTLY' ? periodLength : periodLength + 1
}

export const hasInvoiceInFuture = (invoices?: Types.InvoiceAdminDto[]) => {
  if (!invoices) {
    return false
  }

  return invoices.some((invoice) => isFuture(getInvoiceDates(invoice).startDate))
}
